// RealityAI Agent UI — shared types (PHASE K7).
// Event shape mirrors the K6/P0 contract (events_contract.SseWireFormat +
// agent_events row): {run_id, seq, event_name, data} — data is a JSON object.

/** One parsed agent event (the public contract — data-only). */
export interface AgentEvent {
  run_id: string;
  seq: number;
  event_name: string;
  data: Record<string, unknown>;
}

/**
 * Terminal event names (events_contract.TERMINAL_EVENTS). The UI treats
 * these as authoritative lifecycle transitions (never the cancel response).
 */
export const TERMINAL_EVENTS: ReadonlySet<string> = new Set([
  "run.completed",
  "run.failed",
  "run.cancelled",
]);

/**
 * Map a terminal event name to the RunStatus it implies. The run view keeps
 * the persisted status for non-terminal events and switches to this value
 * when the terminal event arrives (SSE = authoritative, K6 limitation #3).
 */
export function terminalEventStatus(eventName: string): "completed" | "failed" | "cancelled" | null {
  switch (eventName) {
    case "run.completed":
      return "completed";
    case "run.failed":
      return "failed";
    case "run.cancelled":
      return "cancelled";
    default:
      return null;
  }
}

// ---------- RU labels (OQ-1: plain RU strings, no i18n framework) ----------

export const RUN_STATUS_LABELS: Record<string, string> = {
  queued: "В очереди",
  running: "Выполняется",
  waiting_approval: "Ожидает подтверждения",
  paused: "Пауза",
  completed: "Завершён",
  failed: "Ошибка",
  cancelled: "Отменён",
};

export function runStatusLabel(status: string | null | undefined): string {
  if (!status) return "—";
  return RUN_STATUS_LABELS[status] ?? status;
}

export const EVENT_NAME_LABELS: Record<string, string> = {
  "run.started": "Запуск",
  "plan.created": "План",
  "step.started": "Шаг начался",
  "step.completed": "Шаг завершён",
  "model.started": "Модель: запрос",
  "model.completed": "Модель: ответ",
  "message.delta": "Текст",
  "tool.started": "Инструмент: вызов",
  "tool.completed": "Инструмент: результат",
  "approval.required": "Требуется подтверждение",
  "approval.resolved": "Подтверждение принято",
  "artifact.created": "Артефакт",
  "run.completed": "Завершено",
  "run.failed": "Сбой",
  "run.cancelled": "Отменено",
  limit_exceeded: "Лимит",
  error: "Ошибка",
};

export function eventNameLabel(name: string): string {
  return EVENT_NAME_LABELS[name] ?? name;
}

/** Extract a human-readable summary from an event payload (data-only).
 *  Field names match the K3 runtime payloads (verified against live
 *  agent_events rows, not invented): step.* carry {node, step_index},
 *  plan.created {plan: string[]}, model.* {model, purpose, llm_call_index,
 *  duration_ms, tokens}, message.delta {text}. */
export function eventSummary(ev: AgentEvent): string {
  const d = ev.data;
  const s = (v: unknown): string =>
    typeof v === "string" ? v : v == null ? "" : JSON.stringify(v);
  const obj = (v: unknown): Record<string, unknown> | null =>
    v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
  const error = obj(d.error);
  switch (ev.event_name) {
    case "message.delta":
      return s(d.text ?? d.delta ?? d.content);
    case "tool.started":
      return s(d.tool_name ?? d.name) || s(d.args);
    case "tool.completed":
      return s(d.tool_name ?? d.name) || "результат получен";
    case "step.started":
    case "step.completed": {
      const node = s(d.node);
      const idx = d.step_index != null ? ` #${d.step_index}` : "";
      const dur = ev.event_name === "step.completed" && d.duration_ms != null ? ` (${d.duration_ms} мс)` : "";
      return node ? `${node}${idx}${dur}` : "";
    }
    case "plan.created": {
      const plan = Array.isArray(d.plan) ? d.plan : Array.isArray(d.steps) ? d.steps : null;
      return plan ? `план: ${plan.length} шагов` : s(d.summary);
    }
    case "model.started":
      return `${s(d.model || "модель")} · ${s(d.purpose) || "запрос"}`;
    case "model.completed":
      return d.duration_ms != null ? `ответ за ${d.duration_ms} мс` : "ответ получен";
    case "run.failed":
      return s(error?.message ?? d.error ?? d.message) || "ошибка выполнения";
    case "run.completed":
      return s(d.result ?? d.message) || "задача выполнена";
    case "run.cancelled":
      return s(d.reason) || "запуск отменён";
    case "limit_exceeded":
      return s(d.message ?? d.detail);
    case "error":
      return s(d.message) || "ошибка потока";
    default:
      return "";
  }
}

/** Format ISO timestamp → HH:MM:SS (RU locale, user TZ). */
export function formatTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
